import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import axios from "../_API/axiosConfig";
import { getStoredAccessToken } from "../Helpers/authStorage";

const PUSH_FLAG = String(process.env.REACT_APP_ENABLE_PUSH || "1").trim() !== "0";
const isNative = () =>
  typeof Capacitor !== "undefined" &&
  typeof Capacitor.isNativePlatform === "function" &&
  Capacitor.isNativePlatform();

let listenersReady = false;
let foregroundListenersReady = false;
let navigateFn = null;
let pendingAction = null;
let currentContext = { role: "ADMIN", propietarioId: null, tokenJwt: "" };
let lastPushToken = "";
let pendingRegisterReason = "";
let retryTimer = null;
let retryAttempts = 0;
const MAX_RETRIES = 4;
const RETRY_DELAYS_MS = [2500, 6000, 12000, 25000];
let lastPermissionNoticeAt = 0;

const debug = (...args) => console.log("[push]", ...args);

const toPlatform = () => {
  const platform = Capacitor?.getPlatform?.() || "web";
  if (platform === "ios") return "ios";
  if (platform === "android") return "android";
  return "android";
};

const normalizeRole = (role) => {
  const r = String(role || "").toUpperCase();
  if (r === "PROPIETARIO" || r === "OWNER") return "OWNER";
  return "ADMIN";
};

const getAndroidVersion = () => {
  if (toPlatform() !== "android" || typeof navigator === "undefined") return null;
  const ua = String(navigator.userAgent || "");
  const match = ua.match(/Android\s([0-9]+(?:\.[0-9]+)?)/i);
  return match?.[1] || null;
};

const buildTargetRoute = (data, role) => {
  const payload = data || {};
  const type = String(payload.type || "").toUpperCase();
  const owner = role === "OWNER";
  if (payload.screen && String(payload.screen).startsWith("/")) return String(payload.screen);

  if (type === "CLAIM_NEW" || type === "CLAIM_REPLY") {
    const base = owner ? "/propietario/reclamos" : "/panel/servicios/reclamos";
    return payload.claim_id ? `${base}?claim_id=${payload.claim_id}` : base;
  }
  if (type === "LIQUIDACION_NEW") {
    const base = owner ? "/propietario/liquidaciones" : "/panel/liquidacion/listado/informes";
    return payload.periodo_id ? `${base}?periodo_id=${payload.periodo_id}` : base;
  }
  if (type === "SURVEY_NEW") {
    const base = owner ? "/propietario/encuestas" : "/panel/servicios/listado/encuestas";
    return payload.encuesta_id ? `${base}?encuesta_id=${payload.encuesta_id}` : base;
  }
  if (type === "BUDGET_NEW") {
    const base = owner ? "/propietario/presupuestos" : "/panel/servicios/presupuestos";
    return payload.presupuesto_id ? `${base}?presupuesto_id=${payload.presupuesto_id}` : base;
  }
  if (type === "NOTIFICATION") {
    return owner ? "/propietario" : "/dashboard";
  }
  return owner ? "/propietario" : "/dashboard";
};

const emitBrowserEvent = (name, detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const emitPushUserMessage = (message, status = "warning") => {
  const now = Date.now();
  if (now - lastPermissionNoticeAt < 10000) return false;
  lastPermissionNoticeAt = now;
  emitBrowserEvent("notify", {
    id: `push-${status}-${now}`,
    message,
    status,
  });
  return true;
};

// El aviso de permiso DENEGADO se muestra una sola vez por instalación.
const DENIED_NOTICE_KEY = "wam.push.deniedNoticeShown";

const wasDeniedNoticeShown = async () => {
  try {
    const { value } = await Preferences.get({ key: DENIED_NOTICE_KEY });
    return value === "1";
  } catch {
    return false;
  }
};

const markDeniedNoticeShown = async () => {
  try {
    await Preferences.set({ key: DENIED_NOTICE_KEY, value: "1" });
  } catch (error) {
    debug("no se pudo persistir el aviso de permiso denegado", error?.message || error);
  }
};

const normalizePermissionStatus = (permission) =>
  String(permission?.receive || "unknown").toLowerCase();

const isPermissionGranted = (permission) => normalizePermissionStatus(permission) === "granted";
const isPermissionPrompt = (permission) => normalizePermissionStatus(permission).startsWith("prompt");
const isPermissionDenied = (permission) => normalizePermissionStatus(permission) === "denied";

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return {};
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(normalized);
    return JSON.parse(json || "{}");
  } catch {
    return {};
  }
};

const resolvedOwnerId = () => {
  const ctxOwnerId = Number(currentContext?.propietarioId);
  if (Number.isFinite(ctxOwnerId) && ctxOwnerId > 0) return ctxOwnerId;
  const fromToken = Number(decodeJwtPayload(currentContext?.tokenJwt)?.propietario_id);
  if (Number.isFinite(fromToken) && fromToken > 0) return fromToken;
  return null;
};

const clearRetryTimer = () => {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const scheduleRetry = (reason = "retry") => {
  if (retryAttempts >= MAX_RETRIES) {
    debug("register-device retries agotados", { reason, retryAttempts });
    return;
  }
  clearRetryTimer();
  const delay = RETRY_DELAYS_MS[Math.min(retryAttempts, RETRY_DELAYS_MS.length - 1)];
  retryAttempts += 1;
  pendingRegisterReason = reason;
  retryTimer = setTimeout(() => {
    tryRegisterCurrentToken(`retry-${reason}`);
  }, delay);
};

const postRegisterDevice = async ({ token, source = "unknown" }) => {
  const accessToken = currentContext?.tokenJwt || (await getStoredAccessToken());
  if (!accessToken) {
    debug("register-device skip: no access token", { source });
    scheduleRetry("missing-auth");
    return;
  }
  if (!token) {
    debug("register-device skip: push token vacío", { source });
    pendingRegisterReason = "missing-push-token";
    return;
  }

  const role = normalizeRole(currentContext?.role);
  const body = {
    token,
    platform: toPlatform(),
    provider: "fcm",
    role,
  };
  if (role === "OWNER") {
    const ownerId = resolvedOwnerId();
    if (!ownerId) {
      debug("register-device skip OWNER: propietario_id faltante", {
        source,
        ctxOwnerId: currentContext?.propietarioId,
      });
      scheduleRetry("missing-owner-id");
      return;
    }
    body.propietario_id = ownerId;
  }

  debug("register-device request start", { source, role, propietario_id: body.propietario_id || null });
  try {
    await axios.post("/api/push/register-device/", body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    clearRetryTimer();
    retryAttempts = 0;
    pendingRegisterReason = "";
    debug("register-device request success", { source, role });
  } catch (error) {
    debug("register-device request error", {
      source,
      role,
      status: error?.response?.status,
      detail: error?.response?.data || error?.message,
    });
    scheduleRetry("register-request-failed");
  }
};

function tryRegisterCurrentToken(source = "direct-call") {
  if (!lastPushToken) {
    debug("push token aún no disponible", { source });
    pendingRegisterReason = "missing-push-token";
    return;
  }
  postRegisterDevice({ token: lastPushToken, source });
}

const needsPermissionFlow = (reason) =>
  reason === "permission-denied" ||
  reason === "permission-not-granted" ||
  reason === "permission-check-error" ||
  reason === "permission-request-error";

const ensurePushPermissionAndRegister = async ({ source = "init" } = {}) => {
  let permission;
  try {
    permission = await PushNotifications.checkPermissions();
    debug("notification permission status", {
      stage: "check",
      source,
      receive: normalizePermissionStatus(permission),
    });
  } catch (error) {
    pendingRegisterReason = "permission-check-error";
    debug("notification permission check error", { source, error: error?.message || error });
    emitPushUserMessage("No pudimos verificar el permiso de notificaciones.");
    return false;
  }

  if (!isPermissionGranted(permission)) {
    if (isPermissionPrompt(permission) || !isPermissionDenied(permission)) {
      try {
        permission = await PushNotifications.requestPermissions();
        debug("notification permission status", {
          stage: "request",
          source,
          receive: normalizePermissionStatus(permission),
        });
      } catch (error) {
        pendingRegisterReason = "permission-request-error";
        debug("notification permission request error", { source, error: error?.message || error });
        emitPushUserMessage("No se pudo solicitar el permiso de notificaciones.");
        return false;
      }
    }
  }

  if (!isPermissionGranted(permission)) {
    const status = normalizePermissionStatus(permission);
    pendingRegisterReason = status === "denied" ? "permission-denied" : "permission-not-granted";
    debug("notification permission not granted", { source, receive: status });
    const message = "Notificaciones desactivadas. Activá el permiso para recibir avisos.";
    if (status !== "denied") {
      emitPushUserMessage(message);
    } else if (!(await wasDeniedNoticeShown()) && emitPushUserMessage(message)) {
      await markDeniedNoticeShown();
    }
    return false;
  }

  try {
    debug("push register started", { source });
    await PushNotifications.register();
    pendingRegisterReason = "";
    debug("push register success", { source });
    // En algunos dispositivos el evento "registration" tarda o no llega de inmediato.
    // Intentamos también con el último token conocido (si existiera) para no perder el registro.
    tryRegisterCurrentToken(`after-register-call:${source}`);
    return true;
  } catch (error) {
    pendingRegisterReason = "register-call-error";
    debug("push register error", {
      source,
      detail: error?.message || error,
    });
    emitPushUserMessage("No se pudo registrar push en este dispositivo.");
    return false;
  }
};

const handleActionEvent = (event, role) => {
  const payload = event?.notification?.data || event?.data || {};
  const target = buildTargetRoute(payload, role);
  if (navigateFn) {
    navigateFn(target);
    return;
  }
  pendingAction = target;
};

const registerListeners = () => {
  if (listenersReady) return;
  listenersReady = true;

  PushNotifications.addListener("registration", async (token) => {
    try {
      lastPushToken = token?.value || "";
      debug("push token received", {
        hasToken: Boolean(lastPushToken),
        tokenPreview: lastPushToken ? `${String(lastPushToken).slice(0, 12)}...` : null,
      });
      await postRegisterDevice({ token: lastPushToken, source: "registration-listener" });
    } catch (error) {
      debug("error handling registration listener", error);
      scheduleRetry("listener-error");
    }
  });

  PushNotifications.addListener("registrationError", (error) => {
    debug("push register error", {
      source: "registration-listener",
      detail: error?.error || error?.message || error,
    });
    scheduleRetry("registration-error");
  });

  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    emitBrowserEvent("push:received", notification);
  });

  PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    emitBrowserEvent("push:action", event);
    handleActionEvent(event, normalizeRole(currentContext.role));
  });
};

const registerForegroundRetryHooks = () => {
  if (foregroundListenersReady || typeof window === "undefined") return;
  foregroundListenersReady = true;

  const onFocusRetry = () => {
    if (!isNative()) return;
    if (!pendingRegisterReason) return;
    debug("retry on app foreground/focus", { reason: pendingRegisterReason });
    if (needsPermissionFlow(pendingRegisterReason)) {
      ensurePushPermissionAndRegister({ source: "foreground-retry" }).catch((error) => {
        debug("foreground permission/register retry error", error);
      });
      return;
    }
    tryRegisterCurrentToken("foreground-retry");
  };

  window.addEventListener("focus", onFocusRetry);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") onFocusRetry();
  });
};

export const setPushNavigator = (navigate) => {
  navigateFn = navigate;
  if (pendingAction && navigateFn) {
    navigateFn(pendingAction);
    pendingAction = null;
  }
};

export const initPushForCurrentSession = async ({ role, propietarioId, tokenJwt } = {}) => {
  const platform = toPlatform();
  const androidVersion = getAndroidVersion();
  debug("push init start", {
    enabled: PUSH_FLAG,
    isNative: isNative(),
    platform,
    androidVersion,
    role,
    propietarioId: propietarioId || null,
  });
  if (!PUSH_FLAG || !isNative()) return;

  currentContext = {
    role: normalizeRole(role),
    propietarioId: propietarioId || null,
    tokenJwt: tokenJwt || currentContext?.tokenJwt || "",
  };

  registerListeners();
  registerForegroundRetryHooks();
  await ensurePushPermissionAndRegister({ source: "init-session" });
};

// Compatibilidad con código existente
export const initPushManager = initPushForCurrentSession;
